/**
 * User Context Provider
 * 
 * Manages user authentication state, profile data, roles, and subscriptions.
 * Provides centralized user state management across the application with proper error handling.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentUser, type UserRole, type User } from '@/lib/adapters/trackingeats';
import { supabase } from '@/integrations/supabase/client';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';

interface UserContextType {
  user: User | null;
  loading: boolean;
  updateUserRole: (role: UserRole) => void;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoadingUser, setIsLoadingUser] = useState(false);

  const loadUser = async (): Promise<boolean> => {
    if (isLoadingUser) {
      return false;
    }
    
    try {
      setIsLoadingUser(true);
      
      // Get current user from Supabase
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error('User authentication failed:', userError);
        setUser(null);
        setLoading(false);
        return false;
      }

      // Get user profile from database
      let { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (profileError || !profile) {
        // Check if user has admin role in user_roles table before creating profile
        let defaultRole = 'user';
        try {
          const { data: userRole } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (userRole?.role === 'admin') {
            defaultRole = 'admin';
          }
        } catch (error) {
          // If check fails, default to 'user'
          console.warn('Failed to check user role, defaulting to user:', error);
        }
        
        // Create profile with proper error handling
        const { error: createError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            email: user.email || '',
            name: user.email?.split('@')[0] || 'User',
            role: defaultRole
          });
        
        // Create notification preferences with all notifications enabled when profile is created
        if (!createError) {
          try {
            await supabase
              .from('notification_preferences')
              .upsert({
                user_id: user.id,
                // Email preferences - all enabled
                email_onboarding: true,
                email_transactional: true,
                email_promotional: true,
                email_truck_nearby: true,
                email_booking_updates: true,
                email_weekly_digest: true,
                // Push notification preferences - all enabled
                push_enabled: true,
                push_truck_nearby: true,
                push_booking_updates: true,
                // SMS preferences - all enabled
                sms_enabled: true,
                sms_booking_confirmed: true,
                sms_truck_nearby: true,
                sms_payment_received: true,
              }, {
                onConflict: 'user_id'
              });
          } catch (notificationError) {
            // Silently fail - preferences can be created later
            console.warn('Failed to create notification preferences:', notificationError);
          }
        }
        
        if (createError) {
          // Fall back to basic user data
          const userData = {
            id: user.id,
            email: user.email || '',
            name: user.email?.split('@')[0] || 'User',
            role: 'user' as UserRole,
            trucks: [],
            favoriteIds: [],
            subscription: null
          };
          
          setUser(userData);
          setLoading(false);
          return true;
        }
        
        // Role creation is now handled by atomic database functions
        // Just retry profile query
        const { data: newProfile, error: retryError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (retryError || !newProfile) {
          // Fall back to basic user data
          const userData = {
            id: user.id,
            email: user.email || '',
            name: user.email?.split('@')[0] || 'User',
            role: 'user' as UserRole,
            trucks: [],
            favoriteIds: [],
            subscription: null
          };
          
          setUser(userData);
          setLoading(false);
          return true;
        }
        
        profile = newProfile;
      }

      // Get user's trucks (non-blocking)
      let trucks = [];
      try {
        const { data: trucksData } = await supabase
          .from('food_trucks')
          .select('*')
          .eq('owner_id', user.id)
          .eq('is_active', true);

        trucks = trucksData || [];
      } catch (error) {
        // Silently fail for trucks
      }

      // Get subscription status (non-blocking)
      let subscription = null;
      try {
        const { data: subData } = await supabase
          .from('user_subscriptions')
          .select('status, plan_id')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        subscription = subData;
      } catch (error) {
        // Silently fail for subscription
      }

      // Check if subscription status is out of sync and refresh if needed
      if (subscription && profile?.has_non_active_subscription) {
        try {
          await supabase.rpc('refresh_user_subscription_status', { p_user_id: user.id });
          // Reload profile after refresh
          const { data: refreshedProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle();
          if (refreshedProfile) {
            profile = refreshedProfile;
          }
        } catch (error) {
          console.error('Failed to refresh subscription status:', error);
        }
      }

      const isPaid = !!subscription;
      // Role comes from profiles table
      const profileRole = profile?.role || 'user';
      
      // Determine actual role with proper validation
      let actualRole: UserRole = 'user';
      
      if (profileRole === 'admin') {
        actualRole = 'admin';
      } else if (profileRole === 'vendor') {
        actualRole = 'vendor';
      } else {
        actualRole = 'user';
      }

      const userData = {
        id: user.id,
        email: user.email || '',
        name: profile?.name || user.email || '',
        role: actualRole as UserRole,
        image_url: profile?.avatar_url,
        trucks: trucks,
        favoriteIds: [],
        subscription: subscription
      };

      setUser(userData);
      
      // Set Sentry user context for error tracking
      try {
        setSentryUser({
          id: userData.id,
          email: userData.email,
          username: userData.name,
        });
      } catch (error) {
        // Don't let Sentry errors break the app
        console.warn('Failed to set Sentry user context:', error);
      }
      
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Failed to load user:', error);
      setUser(null);
      setLoading(false);
      return false;
    } finally {
      setIsLoadingUser(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    /**
     * Set up auth state listener to handle sign-in/out events
     * and keep user context synchronized with authentication state
     */
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setLoading(true);
          // Defer loadUser to avoid race conditions
          setTimeout(() => {
            loadUser();
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          // Clear Sentry user context on sign out
          try {
            clearSentryUser();
          } catch (error) {
            // Don't let Sentry errors break the app
            console.warn('Failed to clear Sentry user context:', error);
          }
          setLoading(false);
          setIsLoadingUser(false);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Defer loadUser to avoid race conditions
          setTimeout(() => {
            loadUser();
          }, 0);
        } else if (event === 'INITIAL_SESSION') {
          if (session?.user) {
            setLoading(true);
            setTimeout(() => {
              loadUser();
            }, 0);
          } else {
            setLoading(false);
          }
        }
      }
    );

    // Check for existing session on initial load
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setLoading(true);
        loadUser();
      } else {
        setLoading(false);
      }
    });

    // Fail-safe: ensure loading state is cleared after timeout
    const failSafe = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(failSafe);
    };
  }, []);

  /**
   * Updates the user's role in the local context state
   * Note: This only updates the local state, not the database
   */
  const updateUserRole = (role: UserRole) => {
    if (user) {
      const updatedUser = { ...user, role };
      setUser(updatedUser);
    }
  };

  /**
   * Refreshes user data from the database
   * Used after role updates or truck creation to ensure UI reflects latest state
   */
  const refreshUser = async () => {
    await loadUser();
  };

  const value: UserContextType = {
    user,
    loading,
    updateUserRole,
    refreshUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
